import PropTypes from 'prop-types';
import { Icon, ButtonTooltip } from '@evertrue/et-components';

const METRIC_TOOLTIPS = {
  Sent: 'Constituents for whom a send was attempted. Excludes skips.',
  Delivered: 'Sends that successfully reached the inbox.',
  Opened: 'Recipients who opened the email at least once.',
  Clicked: 'Recipients who clicked a link in the email.',
  Replied: 'Recipients who replied to the email.',
  'Success Reached': 'Recipients who met the configured success metric.',
  Skipped: 'Recipients for whom the step was bypassed without a send being attempted.',
  Error: 'Sends that resulted in a delivery failure (bounce).',
  Unsubscribed: 'Recipients who unsubscribed via the email.',
  Exited: 'Recipients who left the cadence at this step for any reason.',
  'Tasks Completed': 'A user marked the manual task as done for this recipient.',
  'Tasks Pending': 'The manual task has not yet been completed for this recipient.',
};

/**
 * Displays a summary row of stat widgets for a selected step.
 * Swaps between email step metrics and manual task metrics based on step type.
 */
const StepStatsSummary = ({ stepData }) => {
  if (!stepData) return null;

  const isManualStep = stepData.type === 'task' || stepData.isAutomated === false;

  const metrics = isManualStep
    ? [
        { label: 'Tasks Completed', value: stepData.taskComplete },
        {
          label: 'Tasks Pending',
          value:
            stepData.taskCreated != null
              ? Math.max(0, (stepData.taskCreated ?? 0) - (stepData.taskComplete ?? 0) - (stepData.tasksSkipped ?? 0))
              : null,
        },
        { label: 'Skipped', value: stepData.skipped },
        { label: 'Success Reached', value: stepData.qualified },
        { label: 'Exited', value: stepData.exited },
      ]
    : [
        { label: 'Sent', value: stepData.sends },
        { label: 'Delivered', value: stepData.delivered },
        { label: 'Opened', value: stepData.opens },
        { label: 'Clicked', value: stepData.clicks },
        { label: 'Replied', value: stepData.replied },
        { label: 'Success Reached', value: stepData.qualified },
        { label: 'Skipped', value: stepData.skipped },
        { label: 'Error', value: stepData.errorCount },
        { label: 'Unsubscribed', value: stepData.unsubscribed },
        { label: 'Exited', value: stepData.exited },
      ];

  return (
    <div className="cadence-detail-page--step-stats-summary">
      {metrics.map(({ label, value }) => (
        <div key={label} className="cadence-detail-page--step-stat">
          <span className="cadence-detail-page--step-stat-value">{value ?? 0}</span>
          <span className="cadence-detail-page--step-stat-label">
            {label}
            {METRIC_TOOLTIPS[label] && (
              <ButtonTooltip
                contentWidth={240}
                trigger={
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Icon icon="info" style={{ fontSize: 14, color: '#939393' }} />
                  </span>
                }
              >
                {METRIC_TOOLTIPS[label]}
              </ButtonTooltip>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};

StepStatsSummary.propTypes = {
  stepData: PropTypes.shape({
    type: PropTypes.string,
    isAutomated: PropTypes.bool,
    completed: PropTypes.number,
    taskComplete: PropTypes.number,
    taskCreated: PropTypes.number,
    tasksSkipped: PropTypes.number,
    sends: PropTypes.number,
    delivered: PropTypes.number,
    opens: PropTypes.number,
    clicks: PropTypes.number,
    replied: PropTypes.number,
    qualified: PropTypes.number,
    skipped: PropTypes.number,
    errorCount: PropTypes.number,
    unsubscribed: PropTypes.number,
    exited: PropTypes.number,
  }),
};

export default StepStatsSummary;
